import { createLoader } from '@meteor/data-hooks';
import { autoPaginate, withManagementSDK } from '../../management';
import { withAuth } from '../../user';
import { portalClient } from '../client';

export let portalsLoader = createLoader({
  name: 'portals',
  fetch: (i: { projectId: string }) =>
    withManagementSDK(i, sdk =>
      autoPaginate(pag => sdk.projects.portals.list(i.projectId, pag))
    ),
  mutators: {
    create: (i: { name: string; type: 'standalone' | 'embedded' }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.create(input.projectId, {
          name: i.name,
          type: i.type
        })
      )
  }
});

export let usePortals = (projectId: string | null | undefined) => {
  let portals = portalsLoader.use(projectId ? { projectId } : null);

  return {
    ...portals,
    createMutator: portals.useMutator('create')
  };
};

export let portalLoader = createLoader({
  name: 'portal',
  parents: [portalsLoader],
  fetch: (i: { projectId: string; portalId: string }) =>
    withManagementSDK(i, sdk => sdk.projects.portals.get(i.projectId, i.portalId)),
  mutators: {
    update: (
      i: {
        name?: string;
        publicName?: string;
      },
      { input }
    ) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.update(input.projectId, input.portalId, i)
      ),

    delete: (_, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.delete(input.projectId, input.portalId)
      )
  }
});

export let usePortal = (
  projectId: string | null | undefined,
  portalId: string | null | undefined
) => {
  let portal = portalLoader.use(portalId && projectId ? { projectId, portalId } : null);

  return {
    ...portal,
    updateMutator: portal.useMutator('update'),
    deleteMutator: portal.useMutator('delete')
  };
};

export let portalDomainsLoader = createLoader({
  name: 'portalDomains',
  parents: [],
  fetch: (i: { projectId: string; portalId: string }) =>
    withManagementSDK(i, sdk =>
      autoPaginate(pag =>
        sdk.projects.portals.portalDomains.list(i.projectId, i.portalId, pag)
      )
    ),
  mutators: {
    createDomain: (i: { domain: string; environmentId: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalDomains.create(input.projectId, input.portalId, {
          domain: i.domain,
          portalEnvironmentId: i.environmentId
        })
      ),

    deleteDomain: (i: { domainId: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalDomains.delete(input.projectId, input.portalId, i.domainId)
      ),

    updateDomain: (i: { environmentId: string; domainId: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalDomains.update(
          input.projectId,
          input.portalId,
          i.domainId,
          {
            portalEnvironmentId: i.environmentId
          }
        )
      ),

    recheckDomain: (i: { domainId: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalDomains.recheck(input.projectId, input.portalId, i.domainId)
      )
  }
});

export let usePortalDomains = (
  projectId: string | null | undefined,
  portalId: string | null | undefined
) => {
  let portal = portalDomainsLoader.use(portalId && projectId ? { projectId, portalId } : null);

  return {
    ...portal,
    createDomainMutator: portal.useMutator('createDomain'),
    deleteDomainMutator: portal.useMutator('deleteDomain'),
    updateDomainMutator: portal.useMutator('updateDomain'),
    recheckDomainMutator: portal.useMutator('recheckDomain')
  };
};

export let portalEnvironmentsLoader = createLoader({
  name: 'portalEnvironments',
  parents: [],
  fetch: (i: { projectId: string; portalId: string }) =>
    withManagementSDK(i, sdk =>
      autoPaginate(pag =>
        sdk.projects.portals.portalEnvironments.list(i.projectId, i.portalId, pag)
      )
    ),
  mutators: {
    createEnvironment: (i: { instanceId: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalEnvironments.create(input.projectId, input.portalId, {
          instanceId: i.instanceId
        })
      ),

    updateEnvironment: (i: { environmentId: string; name?: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalEnvironments.update(
          input.projectId,
          input.portalId,
          i.environmentId,
          {
            name: i.name
          }
        )
      )
  }
});

export let usePortalEnvironments = (
  projectId: string | null | undefined,
  portalId: string | null | undefined
) => {
  let portal = portalEnvironmentsLoader.use(
    portalId && projectId ? { projectId, portalId } : null
  );

  return {
    ...portal,
    createEnvironmentMutator: portal.useMutator('createEnvironment'),
    updateEnvironmentMutator: portal.useMutator('updateEnvironment')
  };
};

export let portalAgentsLoader = createLoader({
  name: 'portalAgents',
  parents: [],
  fetch: (i: { projectId: string; portalId: string }) =>
    withManagementSDK(i, sdk =>
      autoPaginate(pag => sdk.projects.portals.portalAgents.list(i.projectId, i.portalId, pag))
    ),
  mutators: {
    createAgent: (i: { agentId: string; name: string; description: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalAgents.create(input.projectId, input.portalId, {
          agentId: i.agentId,
          name: i.name,
          description: i.description
        })
      ),

    updateAgent: (
      i: { portalAgentId: string; name?: string; description?: string },
      { input }
    ) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalAgents.update(
          input.projectId,
          input.portalId,
          i.portalAgentId,
          {
            name: i.name,
            description: i.description
          }
        )
      ),

    deleteAgent: (i: { portalAgentId: string }, { input }) =>
      withManagementSDK(input, sdk =>
        sdk.projects.portals.portalAgents.delete(
          input.projectId,
          input.portalId,
          i.portalAgentId
        )
      )
  }
});

export let usePortalAgents = (
  projectId: string | null | undefined,
  portalId: string | null | undefined
) => {
  let portal = portalAgentsLoader.use(portalId && projectId ? { projectId, portalId } : null);

  return {
    ...portal,
    createAgentMutator: portal.useMutator('createAgent'),
    updateAgentMutator: portal.useMutator('updateAgent'),
    deleteAgentMutator: portal.useMutator('deleteAgent')
  };
};

export let portalAuthLoader = createLoader({
  name: 'portalAuth',
  parents: [],
  fetch: (i: { instanceId: string; portalId: string }) =>
    withAuth(() => portalClient.portalAuth.get(i)),
  mutators: {
    update: (
      i: {
        authProviders: Parameters<typeof portalClient.portalAuth.update>[0]['authProviders'];
      },
      { input }
    ) =>
      withAuth(() =>
        portalClient.portalAuth.update({
          ...i,
          portalId: input.portalId,
          instanceId: input.instanceId
        })
      )
  }
});

export let usePortalAuth = (
  instanceId: string | null | undefined,
  portalId: string | null | undefined
) => {
  let portal = portalAuthLoader.use(portalId && instanceId ? { instanceId, portalId } : null);

  return {
    ...portal,
    updateAuthMutator: portal.useMutator('update')
  };
};
